import { toBoundedTurnstileErrorCode } from '@/components/Authentication/TurnstileWidget';
import { defer, type Deferred } from '../defer';
import { isWAFFailureBlockingApp } from '../failureStore';
import { runGuarded } from '../runGuarded';
import { recordWAFTelemetry } from '../telemetry';
import {
  WAFClearanceDetail,
  WAFClearanceResolveState,
  WAFClearanceStage,
  WAFProvider,
  WAFTelemetryOutcomeExtra,
  WAFTelemetryPhase,
  type WAFChallengeProvider,
  type WAFProviderClearanceResult,
  type WAFWidgetFailureDetail,
} from '../types';
import { wafChallengeManager } from '../WAFChallengeManager';
import { hasWAFTurnstileSitekey } from './consts';

type Subscriber = () => void;

class CloudflareTurnstileClearanceProvider implements WAFChallengeProvider {
  readonly provider = WAFProvider.Cloudflare;
  private activeClearance: Deferred<WAFProviderClearanceResult> | undefined;
  private isInteractive = false;
  // Whether THIS clearance has already been promoted to the visible overlay once; reset in settle() so the next
  // clearance starts fresh.
  private hasPromotedThisClearance = false;
  private isScriptLoaded = false;
  private readWidgetToken: (() => string | undefined) | undefined;
  private readonly recordedWidgetErrorCodes = new Set<string>();
  private readonly subscribers = new Set<Subscriber>();

  requestClearance = (): Promise<WAFProviderClearanceResult> => {
    if (this.activeClearance) {
      return this.activeClearance.promise;
    }
    if (!hasWAFTurnstileSitekey()) {
      return Promise.resolve({
        state: WAFClearanceResolveState.Unavailable,
        detail: WAFClearanceDetail.MissingSitekey,
      });
    }
    const clearance = defer<WAFProviderClearanceResult>();
    this.activeClearance = clearance;
    this.hasPromotedThisClearance = false;
    this.notify();
    return clearance.promise; // captured: a subscriber that settles during notify() would clear the field
  };

  // Resolve (never reject) so a manager-driven teardown leaves no waiter hanging.
  cancelClearance = (): void => {
    this.settle({ state: WAFClearanceResolveState.Error, detail: WAFClearanceDetail.Cancelled });
  };

  settleWidgetSuccess = (clearanceCookieIssued?: boolean): void => {
    this.settle({ state: WAFClearanceResolveState.Solved, clearanceCookieIssued });
  };

  markScriptLoaded = (wasScriptAlreadyLoaded: boolean): void => {
    if (!this.activeClearance || this.isScriptLoaded) return;
    this.isScriptLoaded = true;
    wafChallengeManager.notifyScriptLoaded(wasScriptAlreadyLoaded);
  };

  markWidgetRendered = (readWidgetToken: () => string | undefined): void => {
    if (!this.activeClearance) return;
    this.readWidgetToken = readWidgetToken;
  };

  describeStage = (): WAFClearanceStage | undefined => {
    if (!this.activeClearance) return undefined;
    if (!this.isScriptLoaded) return WAFClearanceStage.ScriptNotLoaded;
    if (!this.readWidgetToken) return WAFClearanceStage.WidgetNotRendered;
    return this.readWidgetToken() ? WAFClearanceStage.AwaitingClearanceCookie : WAFClearanceStage.AwaitingWidgetToken;
  };

  settleWidgetFailure = (detail: WAFWidgetFailureDetail): void => {
    this.settle({ state: WAFClearanceResolveState.Unavailable, detail });
  };

  enterInteractive = (): void => {
    if (!this.activeClearance || this.isInteractive) return;
    recordWAFTelemetry({
      phase: WAFTelemetryPhase.Clearance,
      outcome: WAFTelemetryOutcomeExtra.WidgetNeedsInteraction,
      wafProvider: this.provider,
      pageVisibilityState: document.visibilityState,
    });
    // Withhold a first promotion when a terminal failure is already on screen: settle Unavailable now (fail fast,
    // releasing single-flight) rather than holding single-flight indefinitely for a challenge we won't show. A
    // re-promotion of an already-shown clearance (Turnstile demotes then re-enters interactive mid-solve) skips the
    // check, so a challenge the user was already shown stays solvable.
    if (!this.hasPromotedThisClearance && isWAFFailureBlockingApp()) {
      this.settle({
        state: WAFClearanceResolveState.Unavailable,
        detail: WAFClearanceDetail.PromotionWithheldFailureShowing,
      });
      return;
    }
    this.hasPromotedThisClearance = true;
    this.isInteractive = true;
    this.notify();
    wafChallengeManager.notifyInteractiveStarted();
  };

  exitInteractive = (): void => {
    if (!this.activeClearance || !this.isInteractive) return;
    recordWAFTelemetry({
      phase: WAFTelemetryPhase.Clearance,
      outcome: WAFTelemetryOutcomeExtra.WidgetAfterInteractive,
      wafProvider: this.provider,
      pageVisibilityState: document.visibilityState,
    });
    this.isInteractive = false;
    this.notify();
    wafChallengeManager.notifyInteractiveEnded();
  };

  // Records a widget error WITHOUT settling, so Cloudflare's own retry:'auto' still runs. Non-retryable
  // codes (e.g. an unauthorized domain) never recover, but this names the cause behind a slow clearance.
  // Once per code per clearance: a clearance never times out, so a widget failing on every retry would
  // otherwise log for as long as the tab stays open.
  recordWidgetError = (providerErrorCode: string): void => {
    if (!this.activeClearance) return;
    const boundedProviderErrorCode = toBoundedTurnstileErrorCode(providerErrorCode);
    if (this.recordedWidgetErrorCodes.has(boundedProviderErrorCode)) return;
    this.recordedWidgetErrorCodes.add(boundedProviderErrorCode);
    recordWAFTelemetry({
      phase: WAFTelemetryPhase.Clearance,
      outcome: WAFTelemetryOutcomeExtra.WidgetError,
      wafProvider: this.provider,
      pageVisibilityState: document.visibilityState,
      providerErrorCode: boundedProviderErrorCode,
    });
  };

  subscribe = (subscriber: Subscriber): (() => void) => {
    this.subscribers.add(subscriber);
    return () => {
      this.subscribers.delete(subscriber);
    };
  };

  /** Both snapshots return a boolean primitive so useSyncExternalStore stays Object.is-stable. */
  getSnapshot = (): boolean => this.activeClearance !== undefined;

  getInteractiveSnapshot = (): boolean => this.isInteractive;

  // Release the waiter before notifying subscribers so a throwing subscriber cannot strand the flow. No
  // outcome telemetry here: the manager's settle records this same clearance event with more context. Must
  // stay throw-free: SilentClearanceErrorBoundary.componentDidCatch calls settleWidgetFailure unguarded, so a
  // throw here would escape to the app ErrorBoundary.
  private settle(result: WAFProviderClearanceResult): void {
    const clearance = this.activeClearance;
    if (!clearance) return; // idempotent: first settle wins
    this.activeClearance = undefined;
    this.isInteractive = false;
    this.hasPromotedThisClearance = false;
    this.isScriptLoaded = false;
    this.readWidgetToken = undefined;
    this.recordedWidgetErrorCodes.clear();
    clearance.resolve(result);
    this.notify();
  }

  private notify(): void {
    this.subscribers.forEach((subscriber) => runGuarded(subscriber));
  }

  // Test-only: settle any active clearance AND drop subscribers, so neither leaks into a later test.
  TestOnlyReset(): void {
    this.cancelClearance();
    this.subscribers.clear();
  }
}

export const cloudflareTurnstileClearanceProvider = new CloudflareTurnstileClearanceProvider();

export const TestOnlyResetCloudflareClearanceProvider = (): void => {
  cloudflareTurnstileClearanceProvider.TestOnlyReset();
};
